import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ClipboardList, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { diseasesAPI } from "@/lib/diseases-api";
import { queryKeys } from "@/lib/query-keys";
import { DiseaseReadField } from "./DiseaseReadField";
import { diseaseDisplayName, diseaseInitial } from "./diseaseUtils";

interface DiseaseDetailViewProps {
  diseaseId: string;
  onBack: () => void;
}

const yesNo = (value?: boolean) => (value ? "Yes" : "No");

export const DiseaseDetailView = ({
  diseaseId,
  onBack,
}: DiseaseDetailViewProps) => {
  const {
    data: disease,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.diseases.detail(diseaseId),
    queryFn: () => diseasesAPI.get(diseaseId),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !disease) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
        <ClipboardList className="mb-2 h-10 w-10 opacity-40" />
        <p className="z-body font-medium">Failed to load disease details.</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <Card className="flex min-h-0 flex-1 flex-col overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onBack}
              aria-label="Back to Diseases"
              className="shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted text-sm font-medium text-muted-foreground">
              {diseaseInitial(diseaseDisplayName(disease))}
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <CardTitle className="z-h2 truncate">
                  {diseaseDisplayName(disease)}
                </CardTitle>
                <Badge variant="secondary">Viewing</Badge>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  variant="secondary"
                  className="border-primary/20 bg-primary/10 text-primary"
                >
                  {disease.short_name || "Disease"}
                </Badge>
                {disease.icd10_code ? (
                  <Badge variant="neutral" className="font-mono">
                    {disease.icd10_code}
                  </Badge>
                ) : null}
                {disease.is_active === false ? (
                  <Badge variant="destructive">Inactive</Badge>
                ) : null}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          <Card>
            <CardContent className="space-y-5 pt-6 pb-5">
              <div className="grid grid-cols-1 gap-x-8 gap-y-4 lg:grid-cols-2">
                <DiseaseReadField label="Long Name" value={disease.long_name} />
                <DiseaseReadField
                  label="Short Name"
                  value={disease.short_name}
                />
                <DiseaseReadField
                  label="Display Name"
                  value={disease.display_name}
                />
                <DiseaseReadField
                  label="ICD-10 Code"
                  value={disease.icd10_code}
                />
              </div>
              <DiseaseReadField
                label="Other Names"
                value={disease.other_names}
                multiline
              />
              <div className="grid grid-cols-1 gap-x-8 gap-y-4 rounded-3xl border border-border p-5 lg:grid-cols-2">
                <DiseaseReadField
                  label="Widely Prevalent"
                  value={yesNo(disease.widely_prevalent)}
                />
                <DiseaseReadField
                  label="Family Linked"
                  value={yesNo(disease.family_linked)}
                />
                <DiseaseReadField
                  label="Featured"
                  value={yesNo(disease.is_featured)}
                />
                <DiseaseReadField
                  label="Active"
                  value={yesNo(disease.is_active)}
                />
                <DiseaseReadField
                  label="Created By"
                  value={disease.created_by}
                />
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};
